import React, { useCallback, useState } from 'react'
import ReactDatePicker from 'react-datepicker'
import { parse, isValid, startOfDay } from 'date-fns'

type DatePickerProps = React.ComponentProps<typeof ReactDatePicker>

/**
 * Wrapper around react-datepicker that all date fields should use instead of
 * importing react-datepicker directly. It adds:
 *
 * 1. Typed date input — intercepts onChangeRaw to parse typed strings with
 *    date-fns, only committing the value when it's a valid complete date.
 *    rawValue is held in local state while typing and cleared once valid.
 *
 * 2. minDate/maxDate enforcement on typing — react-datepicker only blocks
 *    out-of-range clicks in the calendar; typed input bypasses that.
 *    We validate the parsed date against minDate/maxDate before committing.
 *
 * 3. Consistent defaults — year/month dropdowns, scrollable 100-year range,
 *    and 'select' dropdown mode are configured here once for all consumers.
 *
 * 4. Caller hook composition — onChangeRaw and onBlur from consumers are
 *    destructured and forwarded after our internal logic, so Formik
 *    touched-state and any custom handlers still fire correctly.
 *
 * 5. Range mode safety — for selectsRange consumers, raw typing is skipped
 *    entirely (early return) since onChange expects a [Date, Date] tuple.
 */
export const TypeableDatePicker: React.FC<DatePickerProps> = ({
  onChange,
  onChangeRaw: callerOnChangeRaw,
  onBlur: callerOnBlur,
  dateFormat = 'yyyy-MM-dd',
  ...props
}) => {
  const [rawValue, setRawValue] = useState<string | null>(null)

  const formatStr = Array.isArray(dateFormat) ? dateFormat[0] : dateFormat
  const isRange = !!props.selectsRange

  const handleChangeRaw = useCallback(
    (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      if (!event || isRange) {
        callerOnChangeRaw?.(event)
        return
      }
      event.preventDefault()

      const target = event.target as HTMLInputElement
      const typed = target.value
      setRawValue(typed)

      if (typed === '') {
        ;(
          onChange as (
            date: Date | null,
            event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
          ) => void
        )?.(null, event)
        setRawValue(null)
        callerOnChangeRaw?.(event)
        return
      }

      const parsed = parse(typed, formatStr, new Date())
      if (isValid(parsed) && typed.length === formatStr.length) {
        // Respect minDate/maxDate constraints
        if (props.minDate && startOfDay(parsed) < startOfDay(props.minDate)) return
        if (props.maxDate && startOfDay(parsed) > startOfDay(props.maxDate)) return

        setRawValue(null)
        ;(
          onChange as (
            date: Date | null,
            event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
          ) => void
        )?.(parsed, event)
      }
      callerOnChangeRaw?.(event)
    },
    [formatStr, isRange, onChange, props.minDate, props.maxDate, callerOnChangeRaw],
  )

  const handleChange: DatePickerProps['onChange'] = useCallback(
    (...args: Parameters<NonNullable<DatePickerProps['onChange']>>) => {
      setRawValue(null)
      ;(onChange as (...a: typeof args) => void)?.(...args)
    },
    [onChange],
  )

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setRawValue(null)
      callerOnBlur?.(event)
    },
    [callerOnBlur],
  )

  const datePickerProps = {
    showYearDropdown: true,
    showMonthDropdown: true,
    scrollableYearDropdown: true,
    yearDropdownItemNumber: 100,
    dropdownMode: 'select' as const,
    ...props,
    dateFormat,
    onChange: handleChange,
    onChangeRaw: handleChangeRaw,
    onBlur: handleBlur,
    ...(rawValue !== null ? { value: rawValue } : {}),
  } as DatePickerProps

  return <ReactDatePicker {...datePickerProps} />
}

export default TypeableDatePicker
